import Markdown from 'markdown-to-jsx'

import { ButtonLink } from '@/legacy-src/components/ui/button'
import { cn } from '@/legacy-src/lib/utils'

import FAQ from '../FAQ'
import Icon from '../icons/Icon'
import { ServiceComponentNamesRest } from './componentNames'
import GuideSection from './GuideSection'
import RoundedNote from './RoundedNote'
import TextBanner from './TextBanner'
import { getContainerMaxWidth, getOffset } from './utils/contentStyleUtils'

export type SectionProps = {
  __component: string
  id: string
  title: string
  containerWidth: string
  offset: string
  background: string
  items: [
    {
      __component: string
      content: string
    },
    {
      __component: string
      entry: {
        __component: string
        question: string
        answer: string
        iconName: string
      }[]
    },
  ]
}

const assignSections = (components) => {
  let actualSection = null
  const sectionsComponents: SectionProps[] = []

  components.forEach((component) => {
    if (component.__component === ServiceComponentNamesRest.startSection) {
      actualSection = { ...component, items: [] }
      sectionsComponents.push(actualSection)
    } else if (actualSection) {
      actualSection.items.push(component)
    }
  })

  return sectionsComponents
}

const selectComponent = (componentName: string, item, key) => {
  return (
    {
      [ServiceComponentNamesRest.richContent]: item.content && (
        <Markdown className="rich-content w-full text-center" key={key}>
          {item.content}
        </Markdown>
      ),
      [ServiceComponentNamesRest.faq]: <FAQ key={key} faqs={item} />,
      [ServiceComponentNamesRest.guideSection]: <GuideSection key={key} guide={item} />,
      [ServiceComponentNamesRest.textBanner]: <TextBanner key={key} content={item} />,
      [ServiceComponentNamesRest.roundedNote]: <RoundedNote key={key} content={item} />,
      [ServiceComponentNamesRest.buttonLink]: (
        <div key={key} className="my-12 flex justify-center">
          <ButtonLink variant={item.style === 'link' ? 'inline' : 'default'} href={item.buttonLink}>
            {item.text}
            {item.withArrow && <Icon className="ml-6 text-white" icon="arrow" size="sm" />}
          </ButtonLink>
        </div>
      ),
    }[componentName] ?? null
  )
}

const ContentSections = ({ content }) => {
  const sectionsWithComponents = content ? assignSections(content) : null

  if (!content) {
    return <div className="h-10 tablet:h-16 desktop:h-24" />
  }

  return (
    <>
      {sectionsWithComponents.map((item) => {
        const sectionComponent = item
        const maxWidthClass = getContainerMaxWidth(sectionComponent.containerWidth)
        const topPosition = getOffset(sectionComponent.offset)
        const sectionSpacing = topPosition ? 'pb-40' : 'py-28'

        return (
          <div
            key={`${sectionComponent.__component}-${sectionComponent.id}`}
            className={`relative bg-${sectionComponent.background}`}
          >
            <div className="container">
              <div
                className={cn(
                  'strapi-page__content',
                  'mx-auto flex flex-col justify-center',
                  maxWidthClass,
                  topPosition,
                  sectionSpacing,
                )}
              >
                {sectionComponent.title && <h2 className="text-center">{sectionComponent.title}</h2>}
                {sectionComponent.items.map((component, key) => {
                  return selectComponent(component.__component, component, `${component.__component}-${key}`)
                })}
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
}

export default ContentSections
